import { useEffect, useRef, useState, useCallback } from 'react';
import { type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';

import { tokenManager } from '@/lib/api';
import { createAppSocket } from '@/lib/socket';

export function useSupportUsersSocket(activeConversationId: string | undefined) {
  const qc = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const [typing, setTyping] = useState<{ conversationId: string } | null>(null);
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const token = tokenManager.getAccessToken();
    if (!token) return;
    const socket = createAppSocket(token);
    socketRef.current = socket;

    socket.on('user-support:message', (msg: { conversationId: string }) => {
      qc.invalidateQueries({ queryKey: ['user-support-messages', msg.conversationId] });
      qc.invalidateQueries({ queryKey: ['user-support-inbox'] });
      qc.invalidateQueries({ queryKey: ['user-support-my'] });
    });
    socket.on('user-support:unread', () => {
      qc.invalidateQueries({ queryKey: ['user-support-inbox'] });
      qc.invalidateQueries({ queryKey: ['user-support-my'] });
    });
    socket.on('user-support:status-update', (d: { conversationId?: string }) => {
      if (d.conversationId)
        qc.invalidateQueries({ queryKey: ['user-support-messages', d.conversationId] });
    });
    socket.on('user-support:typing:start', (d: { conversationId: string }) => setTyping(d));
    socket.on('user-support:typing:stop', () => setTyping(null));

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [qc]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !activeConversationId) return;
    socket.emit('join:user-support-conversation', activeConversationId);
    return () => {
      socket.emit('leave:user-support-conversation', activeConversationId);
    };
  }, [activeConversationId]);

  const emitTypingStart = useCallback((conversationId: string) => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit('user-support:typing:start', conversationId);
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(
      () => socket.emit('user-support:typing:stop', conversationId),
      3000
    );
  }, []);

  const emitTypingStop = useCallback((conversationId: string) => {
    const socket = socketRef.current;
    if (!socket) return;
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
      typingTimeout.current = null;
    }
    socket.emit('user-support:typing:stop', conversationId);
  }, []);

  const isTyping = useCallback(
    (conversationId: string) => typing?.conversationId === conversationId,
    [typing]
  );

  return { isTyping, emitTypingStart, emitTypingStop };
}
